import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { underwritingAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Calculator, 
  Brain, 
  TrendingUp, 
  AlertTriangle,
  DollarSign,
  Home,
  Loader2,
  CheckCircle2,
  XCircle,
  ArrowRight,
  FileText,
  Trash2,
  Building2,
  Clock,
  Target,
  Shield,
  Info,
  Zap,
  Scale,
  ShieldCheck,
  Landmark
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const UnderwritingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('quick');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [savedUnderwritings, setSavedUnderwritings] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [offerMode, setOfferMode] = useState('balanced');
  const [prefillComps, setPrefillComps] = useState(null);

  // Offer pricing modes with ARV percentages
  const OFFER_MODES = {
    aggressive: { name: 'Aggressive', percent: 70, description: 'Higher offer, more competitive', icon: Zap, color: 'text-red-400' },
    balanced: { name: 'Balanced', percent: 67, description: 'Recommended for most deals', icon: Scale, color: 'text-amber-400' },
    conservative: { name: 'Conservative', percent: 65, description: 'Lower risk, safer margins', icon: ShieldCheck, color: 'text-green-400' },
    institutional: { name: 'Institutional', percent: 62.5, description: '60-65% ARV rule', icon: Landmark, color: 'text-blue-400' }
  };

  // Calculate suggested offer based on ARV and mode
  const calculateSuggestedOffer = (arv, rehabCost = 0) => {
    if (!arv) return null;
    const arvNum = parseFloat(arv);
    const rehabNum = parseFloat(rehabCost) || 0;
    const mode = OFFER_MODES[offerMode];
    // Formula: (ARV × percentage) - Rehab Cost
    const maxOffer = (arvNum * (mode.percent / 100)) - rehabNum;
    return Math.max(0, maxOffer);
  };

  // Handle prefill data from comps search
  useEffect(() => {
    if (location.state?.prefillData) {
      const data = location.state.prefillData;
      
      // Prefill quick form
      setQuickForm(prev => ({
        ...prev,
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zipcode: data.zipcode || '',
        arv_estimate: data.arv_estimate?.toString() || '',
        property_type: data.property_type || 'single_family'
      }));
      
      // Prefill detailed form
      setDetailedForm(prev => ({
        ...prev,
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zipcode: data.zipcode || '',
        arv_estimate: data.arv_estimate?.toString() || '',
        sqft: data.sqft?.toString() || '',
        beds: data.beds?.toString() || '3',
        baths: data.baths?.toString() || '2',
        property_type: data.property_type || 'single_family'
      }));
      
      // Store comps for display
      if (data.comps && data.comps.length > 0) {
        setPrefillComps(data.comps);
      }
      
      toast.success('Property data loaded from comps search!');
    }
  }, [location.state]);

  // Quick form state
  const [quickForm, setQuickForm] = useState({
    address: '',
    city: '',
    state: '',
    zipcode: '',
    purchase_price: '',
    arv_estimate: '',
    property_type: 'single_family'
  });

  // Detailed form state
  const [detailedForm, setDetailedForm] = useState({
    address: '',
    city: '',
    state: '',
    zipcode: '',
    property_type: 'single_family',
    beds: '3',
    baths: '2',
    sqft: '1500',
    year_built: '1990',
    purchase_price: '',
    arv_estimate: '',
    financing_type: 'cash',
    down_payment_percent: '100',
    interest_rate: '0',
    loan_term_months: '0',
    renovation_budget: '',
    renovation_timeline_months: '3',
    contingency_percent: '10',
    monthly_taxes: '300',
    monthly_insurance: '150',
    monthly_utilities: '200',
    monthly_hoa: '0',
    expected_hold_time_months: '6',
    selling_costs_percent: '8',
    notes: ''
  });

  // Manual form state
  const [manualForm, setManualForm] = useState({
    address: '',
    city: '',
    state: '',
    zipcode: '',
    property_type: 'single_family',
    purchase_price: '',
    arv_estimate: '',
    renovation_budget: '',
    max_offer_price: '',
    total_cash_needed: '',
    closing_costs: '',
    holding_costs_total: '',
    expected_profit: '',
    roi_percent: '',
    cash_on_cash_return: '',
    market_risk: '5',
    renovation_risk: '5',
    financing_risk: '3',
    overall_risk: '5',
    flip_timeline_months: '6',
    rental_potential_monthly: '',
    wholesale_potential: '',
    notes: ''
  });

  useEffect(() => {
    fetchSavedUnderwritings();
  }, []);

  const fetchSavedUnderwritings = async () => {
    try {
      const response = await underwritingAPI.getAll();
      setSavedUnderwritings(response.data);
    } catch (error) {
      console.error('Failed to fetch underwritings');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleQuickSubmit = async () => {
    if (!quickForm.address || !quickForm.purchase_price || !quickForm.arv_estimate) {
      toast.error('Please fill in address, purchase price, and ARV estimate');
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const response = await underwritingAPI.quickUnderwrite({
        ...quickForm,
        purchase_price: parseFloat(quickForm.purchase_price),
        arv_estimate: parseFloat(quickForm.arv_estimate)
      });
      setResult(response.data);
      toast.success('Deal underwritten successfully!');
      fetchSavedUnderwritings();
    } catch (error) {
      toast.error('Failed to underwrite deal');
    } finally {
      setLoading(false);
    }
  };

  const handleDetailedSubmit = async () => {
    if (!detailedForm.address || !detailedForm.purchase_price || !detailedForm.arv_estimate || !detailedForm.renovation_budget) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const response = await underwritingAPI.detailedUnderwrite({
        ...detailedForm,
        beds: parseInt(detailedForm.beds),
        baths: parseFloat(detailedForm.baths),
        sqft: parseInt(detailedForm.sqft),
        year_built: parseInt(detailedForm.year_built),
        purchase_price: parseFloat(detailedForm.purchase_price),
        arv_estimate: parseFloat(detailedForm.arv_estimate),
        down_payment_percent: parseFloat(detailedForm.down_payment_percent),
        interest_rate: parseFloat(detailedForm.interest_rate),
        loan_term_months: parseInt(detailedForm.loan_term_months),
        renovation_budget: parseFloat(detailedForm.renovation_budget),
        renovation_timeline_months: parseInt(detailedForm.renovation_timeline_months),
        contingency_percent: parseFloat(detailedForm.contingency_percent),
        monthly_taxes: parseFloat(detailedForm.monthly_taxes),
        monthly_insurance: parseFloat(detailedForm.monthly_insurance),
        monthly_utilities: parseFloat(detailedForm.monthly_utilities),
        monthly_hoa: parseFloat(detailedForm.monthly_hoa),
        expected_hold_time_months: parseInt(detailedForm.expected_hold_time_months),
        selling_costs_percent: parseFloat(detailedForm.selling_costs_percent)
      });
      setResult(response.data);
      toast.success('Detailed underwriting complete!');
      fetchSavedUnderwritings();
    } catch (error) {
      toast.error('Failed to underwrite deal');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!manualForm.address || !manualForm.purchase_price || !manualForm.arv_estimate) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await underwritingAPI.manualUnderwrite({
        ...manualForm,
        purchase_price: parseFloat(manualForm.purchase_price) || 0,
        arv_estimate: parseFloat(manualForm.arv_estimate) || 0,
        renovation_budget: parseFloat(manualForm.renovation_budget) || 0,
        max_offer_price: parseFloat(manualForm.max_offer_price) || null,
        total_cash_needed: parseFloat(manualForm.total_cash_needed) || null,
        closing_costs: parseFloat(manualForm.closing_costs) || null,
        holding_costs_total: parseFloat(manualForm.holding_costs_total) || null,
        expected_profit: parseFloat(manualForm.expected_profit) || null,
        roi_percent: parseFloat(manualForm.roi_percent) || null,
        cash_on_cash_return: parseFloat(manualForm.cash_on_cash_return) || null,
        market_risk: parseInt(manualForm.market_risk) || null,
        renovation_risk: parseInt(manualForm.renovation_risk) || null,
        financing_risk: parseInt(manualForm.financing_risk) || null,
        overall_risk: parseInt(manualForm.overall_risk) || null,
        flip_timeline_months: parseInt(manualForm.flip_timeline_months) || null,
        rental_potential_monthly: parseFloat(manualForm.rental_potential_monthly) || null,
        wholesale_potential: parseFloat(manualForm.wholesale_potential) || null
      });
      setResult(response.data);
      toast.success('Manual underwriting saved!');
      fetchSavedUnderwritings();
    } catch (error) {
      toast.error('Failed to save underwriting');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToProject = async (underwriteId) => {
    try {
      const response = await underwritingAPI.convertToProject(underwriteId);
      toast.success('Project created from underwriting!');
      navigate(`/projects/${response.data.project_id}`);
    } catch (error) {
      toast.error('Failed to convert to project');
    }
  };

  const handleDeleteUnderwriting = async (underwriteId) => {
    try {
      await underwritingAPI.delete(underwriteId);
      toast.success('Underwriting deleted');
      fetchSavedUnderwritings();
      if (result?.id === underwriteId) {
        setResult(null);
      }
    } catch (error) {
      toast.error('Failed to delete underwriting');
    }
  };

  const getVerdictColor = (verdict) => {
    if (!verdict) return 'text-muted-foreground';
    const v = verdict.toLowerCase();
    if (v.includes('strong buy') || v.includes('buy')) return 'text-green-400';
    if (v.includes('hold')) return 'text-amber-400';
    return 'text-red-400';
  };

  const getRiskColor = (risk) => {
    if (risk <= 3) return 'bg-green-500';
    if (risk <= 6) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

  return (
    <div className="space-y-6" data-testid="underwriting-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold font-heading tracking-tight">Deal Underwriting</h1>
          <p className="text-muted-foreground mt-1">AI-powered investment analysis for fix & flip deals</p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm">
        <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-amber-400">Disclaimer:</span> DealVault provides analytical tools and AI-generated estimates for informational purposes only. All investment decisions remain the sole responsibility of the user.
        </p>
      </div>

      {/* Offer Pricing Calculator */}
      <Card className="border-border bg-card rounded-sm">
        <CardHeader className="border-b border-border pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Suggested Offer Calculator
              </CardTitle>
              <CardDescription>Calculate max offer based on ARV percentage rules</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Mode Selection */}
          <div className="mb-6">
            <Label className="text-sm font-medium mb-3 block">Investment Strategy Mode</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(OFFER_MODES).map(([key, mode]) => {
                const Icon = mode.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setOfferMode(key)}
                    className={`p-4 rounded-sm border transition-all text-left ${
                      offerMode === key
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-secondary/30 hover:border-primary/50'
                    }`}
                    data-testid={`offer-mode-${key}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`w-4 h-4 ${mode.color}`} />
                      <span className="font-semibold text-sm">{mode.name}</span>
                    </div>
                    <p className="text-2xl font-bold font-mono text-primary">{mode.percent}%</p>
                    <p className="text-xs text-muted-foreground mt-1">{mode.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Offer Calculator */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-secondary/30 rounded-sm">
            <div>
              <Label className="text-xs text-muted-foreground">ARV (After Repair Value)</Label>
              <p className="text-xl font-mono font-bold">
                {quickForm.arv_estimate ? formatCurrency(parseFloat(quickForm.arv_estimate)) : '$0'}
              </p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">× {OFFER_MODES[offerMode].percent}% Rule</Label>
              <p className="text-xl font-mono font-bold text-muted-foreground">
                {quickForm.arv_estimate ? formatCurrency(parseFloat(quickForm.arv_estimate) * (OFFER_MODES[offerMode].percent / 100)) : '$0'}
              </p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">− Est. Rehab Cost</Label>
              <p className="text-xl font-mono font-bold text-red-400">
                {detailedForm.renovation_budget ? `-${formatCurrency(parseFloat(detailedForm.renovation_budget))}` : '$0'}
              </p>
            </div>
            <div className="border-l border-border pl-4">
              <Label className="text-xs text-muted-foreground">Max Suggested Offer</Label>
              <p className="text-2xl font-mono font-bold text-green-400">
                {calculateSuggestedOffer(quickForm.arv_estimate, detailedForm.renovation_budget) 
                  ? formatCurrency(calculateSuggestedOffer(quickForm.arv_estimate, detailedForm.renovation_budget))
                  : '$0'}
              </p>
              <p className="text-xs text-muted-foreground">Based on {OFFER_MODES[offerMode].name} mode</p>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
            <Info className="w-3 h-3" />
            Enter ARV in the Quick Analysis form below to see calculated offer suggestions
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Forms */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-border bg-card rounded-sm">
            <CardHeader className="border-b border-border">
              <CardTitle className="font-heading flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary" />
                Underwrite a Deal
              </CardTitle>
              <CardDescription>Choose your analysis method</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-secondary/50 border border-border rounded-sm p-1 mb-6">
                  <TabsTrigger value="quick" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <Brain className="w-4 h-4 mr-2" />
                    AI Quick
                  </TabsTrigger>
                  <TabsTrigger value="detailed" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <FileText className="w-4 h-4 mr-2" />
                    AI Detailed
                  </TabsTrigger>
                  <TabsTrigger value="manual" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <Calculator className="w-4 h-4 mr-2" />
                    Manual
                  </TabsTrigger>
                </TabsList>

                {/* Quick Analysis Tab */}
                <TabsContent value="quick" className="space-y-4">
                  <p className="text-sm text-muted-foreground">Enter basic property info - AI will estimate the rest</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>Property Address *</Label>
                      <Input
                        value={quickForm.address}
                        onChange={(e) => setQuickForm(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="123 Main Street"
                        className="bg-input/50 border-border rounded-sm"
                        data-testid="quick-address-input"
                      />
                    </div>
                    <div>
                      <Label>City *</Label>
                      <Input
                        value={quickForm.city}
                        onChange={(e) => setQuickForm(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="Austin"
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label>State</Label>
                        <Select value={quickForm.state} onValueChange={(value) => setQuickForm(prev => ({ ...prev, state: value }))}>
                          <SelectTrigger className="bg-input/50 border-border rounded-sm">
                            <SelectValue placeholder="ST" />
                          </SelectTrigger>
                          <SelectContent className="bg-popover border-border rounded-sm max-h-[200px]">
                            {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Zip</Label>
                        <Input
                          value={quickForm.zipcode}
                          onChange={(e) => setQuickForm(prev => ({ ...prev, zipcode: e.target.value }))}
                          placeholder="78701"
                          className="bg-input/50 border-border rounded-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Purchase Price *</Label>
                      <Input
                        type="number"
                        value={quickForm.purchase_price}
                        onChange={(e) => setQuickForm(prev => ({ ...prev, purchase_price: e.target.value }))}
                        placeholder="250000"
                        className="bg-input/50 border-border rounded-sm"
                        data-testid="quick-price-input"
                      />
                    </div>
                    <div>
                      <Label>ARV Estimate *</Label>
                      <Input
                        type="number"
                        value={quickForm.arv_estimate}
                        onChange={(e) => setQuickForm(prev => ({ ...prev, arv_estimate: e.target.value }))}
                        placeholder="350000"
                        className="bg-input/50 border-border rounded-sm"
                        data-testid="quick-arv-input"
                      />
                    </div>
                    <div>
                      <Label>Property Type</Label>
                      <Select value={quickForm.property_type} onValueChange={(value) => setQuickForm(prev => ({ ...prev, property_type: value }))}>
                        <SelectTrigger className="bg-input/50 border-border rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border rounded-sm">
                          <SelectItem value="single_family">Single Family</SelectItem>
                          <SelectItem value="multi_family">Multi Family</SelectItem>
                          <SelectItem value="condo">Condo</SelectItem>
                          <SelectItem value="townhouse">Townhouse</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button
                    onClick={handleQuickSubmit}
                    disabled={loading}
                    className="w-full bg-primary text-primary-foreground rounded-sm h-11 btn-scale"
                    data-testid="quick-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing Deal...
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4 mr-2" />
                        AI Underwrite Deal
                      </>
                    )}
                  </Button>
                </TabsContent>

                {/* Detailed Analysis Tab */}
                <TabsContent value="detailed" className="space-y-6">
                  <p className="text-sm text-muted-foreground">Provide full details for comprehensive analysis</p>
                  
                  {/* Property Details */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm flex items-center gap-2"><Home className="w-4 h-4" /> Property Details</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="col-span-2">
                        <Label>Address *</Label>
                        <Input value={detailedForm.address} onChange={(e) => setDetailedForm(prev => ({ ...prev, address: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>City</Label>
                        <Input value={detailedForm.city} onChange={(e) => setDetailedForm(prev => ({ ...prev, city: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>ST</Label>
                          <Select value={detailedForm.state} onValueChange={(value) => setDetailedForm(prev => ({ ...prev, state: value }))}>
                            <SelectTrigger className="bg-input/50 border-border rounded-sm"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-popover border-border rounded-sm max-h-[200px]">
                              {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Zip</Label>
                          <Input value={detailedForm.zipcode} onChange={(e) => setDetailedForm(prev => ({ ...prev, zipcode: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                        </div>
                      </div>
                      <div>
                        <Label>Beds</Label>
                        <Input type="number" value={detailedForm.beds} onChange={(e) => setDetailedForm(prev => ({ ...prev, beds: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Baths</Label>
                        <Input type="number" step="0.5" value={detailedForm.baths} onChange={(e) => setDetailedForm(prev => ({ ...prev, baths: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Sqft</Label>
                        <Input type="number" value={detailedForm.sqft} onChange={(e) => setDetailedForm(prev => ({ ...prev, sqft: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Year Built</Label>
                        <Input type="number" value={detailedForm.year_built} onChange={(e) => setDetailedForm(prev => ({ ...prev, year_built: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Financial Details */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm flex items-center gap-2"><DollarSign className="w-4 h-4" /> Financial Details</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Purchase Price *</Label>
                        <Input type="number" value={detailedForm.purchase_price} onChange={(e) => setDetailedForm(prev => ({ ...prev, purchase_price: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>ARV Estimate *</Label>
                        <Input type="number" value={detailedForm.arv_estimate} onChange={(e) => setDetailedForm(prev => ({ ...prev, arv_estimate: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Renovation Budget *</Label>
                        <Input type="number" value={detailedForm.renovation_budget} onChange={(e) => setDetailedForm(prev => ({ ...prev, renovation_budget: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Financing Type</Label>
                        <Select value={detailedForm.financing_type} onValueChange={(value) => setDetailedForm(prev => ({ ...prev, financing_type: value }))}>
                          <SelectTrigger className="bg-input/50 border-border rounded-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-popover border-border rounded-sm">
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="hard_money">Hard Money</SelectItem>
                            <SelectItem value="conventional">Conventional</SelectItem>
                            <SelectItem value="private">Private Money</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Down Payment %</Label>
                        <Input type="number" value={detailedForm.down_payment_percent} onChange={(e) => setDetailedForm(prev => ({ ...prev, down_payment_percent: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Interest Rate %</Label>
                        <Input type="number" step="0.1" value={detailedForm.interest_rate} onChange={(e) => setDetailedForm(prev => ({ ...prev, interest_rate: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Holding Costs */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm flex items-center gap-2"><Clock className="w-4 h-4" /> Monthly Holding Costs</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Taxes</Label>
                        <Input type="number" value={detailedForm.monthly_taxes} onChange={(e) => setDetailedForm(prev => ({ ...prev, monthly_taxes: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Insurance</Label>
                        <Input type="number" value={detailedForm.monthly_insurance} onChange={(e) => setDetailedForm(prev => ({ ...prev, monthly_insurance: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Utilities</Label>
                        <Input type="number" value={detailedForm.monthly_utilities} onChange={(e) => setDetailedForm(prev => ({ ...prev, monthly_utilities: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>HOA</Label>
                        <Input type="number" value={detailedForm.monthly_hoa} onChange={(e) => setDetailedForm(prev => ({ ...prev, monthly_hoa: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Exit */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm flex items-center gap-2"><Target className="w-4 h-4" /> Exit Strategy</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Reno Timeline (months)</Label>
                        <Input type="number" value={detailedForm.renovation_timeline_months} onChange={(e) => setDetailedForm(prev => ({ ...prev, renovation_timeline_months: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Hold Time (months)</Label>
                        <Input type="number" value={detailedForm.expected_hold_time_months} onChange={(e) => setDetailedForm(prev => ({ ...prev, expected_hold_time_months: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Selling Costs %</Label>
                        <Input type="number" value={detailedForm.selling_costs_percent} onChange={(e) => setDetailedForm(prev => ({ ...prev, selling_costs_percent: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleDetailedSubmit}
                    disabled={loading}
                    className="w-full bg-primary text-primary-foreground rounded-sm h-11 btn-scale"
                    data-testid="detailed-submit-btn"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Brain className="w-4 h-4 mr-2" /> Run Detailed Analysis</>
                    )}
                  </Button>
                </TabsContent>

                {/* Manual Tab */}
                <TabsContent value="manual" className="space-y-6">
                  <p className="text-sm text-muted-foreground">Enter your own calculations</p>
                  
                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm">Property Info</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Address *</Label>
                        <Input value={manualForm.address} onChange={(e) => setManualForm(prev => ({ ...prev, address: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>City</Label>
                        <Input value={manualForm.city} onChange={(e) => setManualForm(prev => ({ ...prev, city: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>ST</Label>
                          <Select value={manualForm.state} onValueChange={(value) => setManualForm(prev => ({ ...prev, state: value }))}>
                            <SelectTrigger className="bg-input/50 border-border rounded-sm"><SelectValue placeholder="ST" /></SelectTrigger>
                            <SelectContent className="bg-popover border-border rounded-sm max-h-[200px]">
                              {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Zip</Label>
                          <Input value={manualForm.zipcode} onChange={(e) => setManualForm(prev => ({ ...prev, zipcode: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                        </div>
                      </div>
                      <div>
                        <Label>Purchase Price *</Label>
                        <Input type="number" value={manualForm.purchase_price} onChange={(e) => setManualForm(prev => ({ ...prev, purchase_price: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>ARV *</Label>
                        <Input type="number" value={manualForm.arv_estimate} onChange={(e) => setManualForm(prev => ({ ...prev, arv_estimate: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Renovation Budget</Label>
                        <Input type="number" value={manualForm.renovation_budget} onChange={(e) => setManualForm(prev => ({ ...prev, renovation_budget: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm">Your Calculations</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Max Offer</Label>
                        <Input type="number" value={manualForm.max_offer_price} onChange={(e) => setManualForm(prev => ({ ...prev, max_offer_price: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Cash Needed</Label>
                        <Input type="number" value={manualForm.total_cash_needed} onChange={(e) => setManualForm(prev => ({ ...prev, total_cash_needed: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Expected Profit</Label>
                        <Input type="number" value={manualForm.expected_profit} onChange={(e) => setManualForm(prev => ({ ...prev, expected_profit: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>ROI %</Label>
                        <Input type="number" value={manualForm.roi_percent} onChange={(e) => setManualForm(prev => ({ ...prev, roi_percent: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm">Risk Assessment (1-10)</h4>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label>Market</Label>
                        <Input type="number" min="1" max="10" value={manualForm.market_risk} onChange={(e) => setManualForm(prev => ({ ...prev, market_risk: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Renovation</Label>
                        <Input type="number" min="1" max="10" value={manualForm.renovation_risk} onChange={(e) => setManualForm(prev => ({ ...prev, renovation_risk: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Financing</Label>
                        <Input type="number" min="1" max="10" value={manualForm.financing_risk} onChange={(e) => setManualForm(prev => ({ ...prev, financing_risk: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                      <div>
                        <Label>Overall</Label>
                        <Input type="number" min="1" max="10" value={manualForm.overall_risk} onChange={(e) => setManualForm(prev => ({ ...prev, overall_risk: e.target.value }))} className="bg-input/50 border-border rounded-sm" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <Textarea value={manualForm.notes} onChange={(e) => setManualForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Your notes on this deal..." className="bg-input/50 border-border rounded-sm" />
                  </div>

                  <Button
                    onClick={handleManualSubmit}
                    disabled={loading}
                    className="w-full bg-primary text-primary-foreground rounded-sm h-11 btn-scale"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
                    Save Manual Underwriting
                  </Button>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Results */}
          {result && (
            <Card className="border-border bg-card rounded-sm amber-glow" data-testid="underwriting-result">
              <CardHeader className="border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      Analysis Results
                    </CardTitle>
                    <CardDescription>{result.property?.address}, {result.property?.city}, {result.property?.state}</CardDescription>
                  </div>
                  {result.analysis?.recommendation && (
                    <div className="text-right">
                      <Badge variant="outline" className={`text-lg px-4 py-1 ${getVerdictColor(result.analysis.recommendation.verdict)}`}>
                        {result.analysis.recommendation.verdict}
                      </Badge>
                      <p className="text-2xl font-bold font-mono mt-1">{result.analysis.recommendation.deal_score}/100</p>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Purchase Analysis */}
                {result.analysis?.purchase_analysis && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-400" /> Purchase Analysis</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Max Offer</p>
                        <p className="font-mono font-bold text-lg">{formatCurrency(result.analysis.purchase_analysis.max_offer_price)}</p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Closing Costs</p>
                        <p className="font-mono font-bold text-lg">{formatCurrency(result.analysis.purchase_analysis.estimated_closing_costs || result.analysis.purchase_analysis.total_closing_costs)}</p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Renovation Est.</p>
                        <p className="font-mono font-bold text-lg">{formatCurrency(result.analysis.purchase_analysis.estimated_renovation || result.analysis.renovation_analysis?.total_renovation)}</p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Total Cash Needed</p>
                        <p className="font-mono font-bold text-lg text-amber-400">{formatCurrency(result.analysis.purchase_analysis.total_cash_needed || result.analysis.purchase_analysis.total_project_cost)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* ROI Calculations */}
                {result.analysis?.roi_calculations && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-400" /> ROI Analysis</h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Expected Profit</p>
                        <p className={`font-mono font-bold text-lg ${(result.analysis.roi_calculations.expected_profit || result.analysis.roi_calculations.net_profit) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatCurrency(result.analysis.roi_calculations.expected_profit || result.analysis.roi_calculations.net_profit)}
                        </p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">ROI</p>
                        <p className="font-mono font-bold text-lg">{result.analysis.roi_calculations.roi_percent?.toFixed(1)}%</p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Cash-on-Cash</p>
                        <p className="font-mono font-bold text-lg">{result.analysis.roi_calculations.cash_on_cash_return?.toFixed(1)}%</p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Profit Margin</p>
                        <p className="font-mono font-bold text-lg">{result.analysis.roi_calculations.profit_margin_percent?.toFixed(1)}%</p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Annualized</p>
                        <p className="font-mono font-bold text-lg">{result.analysis.roi_calculations.annualized_return?.toFixed(1)}%</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Risk Assessment */}
                {result.analysis?.risk_assessment && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-amber-400" /> Risk Assessment</h4>
                    <div className="grid grid-cols-4 gap-4">
                      {['market', 'renovation', 'financing', 'overall'].map(risk => (
                        <div key={risk} className="p-3 bg-secondary/50 rounded-sm">
                          <p className="text-xs text-muted-foreground capitalize">{risk} Risk</p>
                          <div className="flex items-center gap-2 mt-1">
                            <div className={`w-3 h-3 rounded-full ${getRiskColor(result.analysis.risk_assessment[`${risk}_risk`])}`} />
                            <p className="font-mono font-bold">{result.analysis.risk_assessment[`${risk}_risk`]}/10</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {result.analysis.risk_assessment.risk_factors && (
                      <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
                        <p className="text-xs text-red-400 font-semibold mb-1">Risk Factors:</p>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          {result.analysis.risk_assessment.risk_factors.map((f, i) => <li key={i}>• {f}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Exit Strategy */}
                {result.analysis?.exit_strategy && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-purple-400" /> Exit Strategy</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Flip Timeline</p>
                        <p className="font-mono font-bold">{result.analysis.exit_strategy.flip_timeline_months || result.analysis.exit_strategy.flip_analysis?.timeline} months</p>
                        <p className="text-xs text-green-400">{formatCurrency(result.analysis.exit_strategy.flip_profit_estimate || result.analysis.exit_strategy.flip_analysis?.profit)} profit</p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Rental Potential</p>
                        <p className="font-mono font-bold">{formatCurrency(result.analysis.exit_strategy.rental_potential_monthly || result.analysis.exit_strategy.rental_analysis?.monthly_rent)}/mo</p>
                        <p className="text-xs text-muted-foreground">{result.analysis.exit_strategy.rental_cap_rate || result.analysis.exit_strategy.rental_analysis?.cap_rate}% cap rate</p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Wholesale</p>
                        <p className="font-mono font-bold">{formatCurrency(result.analysis.exit_strategy.wholesale_potential || result.analysis.exit_strategy.wholesale_analysis?.assignment_fee)}</p>
                        <p className="text-xs text-muted-foreground">assignment fee</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recommendation */}
                {result.analysis?.recommendation?.executive_summary && (
                  <div className="p-4 bg-primary/10 border border-primary/30 rounded-sm">
                    <h4 className="font-semibold mb-2">Executive Summary</h4>
                    <p className="text-sm">{result.analysis.recommendation.executive_summary || result.analysis.recommendation.summary}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-border">
                  <Button onClick={() => handleConvertToProject(result.id)} className="flex-1 bg-primary text-primary-foreground rounded-sm">
                    <Building2 className="w-4 h-4 mr-2" />
                    Convert to Project
                  </Button>
                  <Button variant="outline" onClick={() => setResult(null)} className="rounded-sm">
                    Clear Results
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* History Sidebar */}
        <div className="space-y-4">
          <Card className="border-border bg-card rounded-sm">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg font-heading">Recent Underwritings</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {historyLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                </div>
              ) : savedUnderwritings.length > 0 ? (
                <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
                  {savedUnderwritings.slice(0, 10).map((uw) => (
                    <div key={uw.id} className="p-4 hover:bg-secondary/30 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{uw.input?.address || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{uw.input?.city}, {uw.input?.state}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="rounded-sm text-xs">{uw.type}</Badge>
                            {uw.analysis?.recommendation?.verdict && (
                              <span className={`text-xs ${getVerdictColor(uw.analysis.recommendation.verdict)}`}>
                                {uw.analysis.recommendation.verdict}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => setResult(uw)} className="h-8 w-8">
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteUnderwriting(uw.id)} className="h-8 w-8 text-muted-foreground hover:text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Calculator className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground">No underwritings yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UnderwritingPage;
